import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { type UserPreference } from "gitpod-next-api/gitpod/v1/user_pb";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { useEventInterest } from "@/hooks/use-event-interest";
import type { GitpodAPI } from "@/api";
import { Code, ConnectError } from "@connectrpc/connect";

export type PlainUserPreference = PlainMessage<UserPreference>;

export function toPlainUserPreference(pref: UserPreference): PlainUserPreference {
    return toPlainMessage(pref);
}

export const userPreferencesQueryKey = {
    list: () => keyWithPrincipal(["preferences", "list"]),
    get: (id?: string) => keyWithPrincipal(["preferences", { id }]),
    getByKey: (key: string) => keyWithPrincipal(["preferences", { key }]),
};

export const handleUserPreferenceEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    if (evt.operation === ResourceOperation.UPDATE) {
        const userPref = await refetchUserPreference(api, evt.resourceId);
        const plain = userPref ? toPlainUserPreference(userPref) : undefined;
        setUserPreferenceInCache(client, evt.resourceId, plain);
        if (plain?.key) {
            client.setQueryData(userPreferencesQueryKey.getByKey(plain.key), plain);
        }
    }
    if (evt.operation === ResourceOperation.CREATE) {
        await client.invalidateQueries({ queryKey: userPreferencesQueryKey.list() });
        // Invalidate all getByKey queries so newly created preferences are picked up
        await client.invalidateQueries({ queryKey: keyWithPrincipal(["preferences"]) });
    }
    if (evt.operation === ResourceOperation.DELETE) {
        setUserPreferenceInCache(client, evt.resourceId, undefined);
        // Invalidate all getByKey queries since we don't know the key from the event
        await client.invalidateQueries({ queryKey: keyWithPrincipal(["preferences"]) });
    }
};

const refetchUserPreference = async (api: GitpodAPI, Id: string): Promise<UserPreference | undefined> => {
    try {
        const response = await api.userService.getPreference({ userPreferenceId: Id });
        return response.preference;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch user preference", Id);
        }
    }
};

function setUserPreferenceInCache(client: QueryClient, Id: string, pref?: PlainUserPreference) {
    client.setQueryData(userPreferencesQueryKey.get(Id), pref);
    client.setQueryData(userPreferencesQueryKey.list(), (currentData?: { preferences: PlainUserPreference[] }) => {
        if (!currentData) {
            return currentData;
        }
        return {
            ...currentData,
            preferences: currentData.preferences
                .map((p) => {
                    if (p.id !== Id) {
                        return p;
                    }
                    return pref;
                })
                .filter((w) => !!w),
        };
    });
}

export type CachedUserPreferenceList = {
    preferences: PlainUserPreference[];
};

export const useSetPreference = () => {
    const client = useQueryClient();
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ key, value }: { key: string; value: string }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            return await api.userService.setPreference({
                key,
                value,
            });
        },
        onMutate: async ({ key, value }) => {
            await client.cancelQueries({ queryKey: userPreferencesQueryKey.list() });
            await client.cancelQueries({ queryKey: userPreferencesQueryKey.getByKey(key) });
            const previousPreferences = client.getQueryData<CachedUserPreferenceList>(userPreferencesQueryKey.list());
            const previousPreference = client.getQueryData<PlainUserPreference>(userPreferencesQueryKey.getByKey(key));
            client.setQueryData(userPreferencesQueryKey.list(), (currentData?: CachedUserPreferenceList) => {
                if (!currentData) {
                    return currentData;
                }

                return {
                    ...currentData,
                    preferences: currentData.preferences.map((previousPreference) => {
                        if (previousPreference.key !== key) {
                            return previousPreference;
                        }

                        return { ...previousPreference, value };
                    }),
                };
            });
            client.setQueryData(userPreferencesQueryKey.getByKey(key), (currentData?: PlainUserPreference) => {
                if (!currentData) {
                    return currentData;
                }

                return { ...currentData, value };
            });

            return { previousPreferences, previousPreference };
        },
        onError: (_error, { key }, context) => {
            client.setQueryData(userPreferencesQueryKey.list(), (currentData?: CachedUserPreferenceList) => {
                if (!currentData) {
                    return currentData;
                }

                return {
                    ...currentData,
                    preferences: currentData.preferences.map((previousPreference) => {
                        if (previousPreference.key !== key) {
                            return previousPreference;
                        }

                        return {
                            ...previousPreference,
                            value: context?.previousPreferences?.preferences.find((p) => p.key === key)?.value,
                        };
                    }),
                };
            });
            client.setQueryData(userPreferencesQueryKey.getByKey(key), (currentData?: PlainUserPreference) => {
                if (!currentData) {
                    return currentData;
                }

                return {
                    ...currentData,
                    value: context?.previousPreference?.value,
                };
            });
        },
        onSettled: async (_, __, { key }) => {
            await client.invalidateQueries({ queryKey: userPreferencesQueryKey.list() });
            await client.invalidateQueries({ queryKey: userPreferencesQueryKey.getByKey(key) });
        },
        throwOnError: defaultThrowOnError,
        retry: (failureCount, error) => {
            if (failureCount < 3) {
                // Retry on "AlreadyExists" do make sure we successfully write preferences even in cases of backend races
                if (error instanceof ConnectError && error.code === Code.AlreadyExists) {
                    return true;
                }
            }
            return false;
        },
        retryDelay: (failureCount, _) => {
            return 500 * failureCount;
        },
    });
};

export const useGetPreference = (id: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const query = useQuery({
        queryKey: userPreferencesQueryKey.get(id),
        // TanStack Query treats `undefined` as "no query data", so encode "not found"
        // as `null` in the cache and normalize it back for callers.
        queryFn: async (): Promise<PlainUserPreference | null> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            try {
                const { preference } = await api.userService.getPreference({ userPreferenceId: id });
                return preference ? toPlainUserPreference(preference) : null;
            } catch (e) {
                if (e instanceof ConnectError && e.code === Code.NotFound) {
                    return null;
                }
                throw e;
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user && !!id,
        staleTime: 1_000 * 10, // 10 seconds
        gcTime: 1_000 * 60 * 60 * 24, // 24 hours
    });
    return { ...query, data: query.data ?? undefined };
};

export const useGetPreferenceByKey = (key: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    useEventInterest({ resourceType: ResourceType.USER_PREFERENCE });

    const query = useQuery({
        queryKey: userPreferencesQueryKey.getByKey(key),
        queryFn: async (): Promise<PlainUserPreference | null> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            try {
                const { preference } = await api.userService.getPreference({
                    preference: { case: "preferenceKey", value: key },
                });
                return preference ? toPlainUserPreference(preference) : null;
            } catch (e) {
                if (e instanceof ConnectError && e.code === Code.NotFound) {
                    return null;
                }
                throw e;
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user && !!key,
        staleTime: 1_000 * 10, // 10 seconds
        gcTime: 1_000 * 60 * 60 * 24, // 24 hours
    });
    return { ...query, data: query.data ?? undefined };
};

export const useDeletePreference = () => {
    const client = useQueryClient();
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ id }: { id: string }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            return await api.userService.deletePreference({ userPreferenceId: id });
        },
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: userPreferencesQueryKey.list() });
            // Invalidate all getByKey queries since we don't know the key from the id
            await client.invalidateQueries({ queryKey: keyWithPrincipal(["preferences"]) });
        },
    });
};
